import {
    ChartShapeInfoConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-shape-info-converter';
import {
    ChartTitleConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/title/chart-title-converter';
import {
    ChartLegendConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/legend/chart-legend-converter';
import {
    ChartPlotAreaConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/plot-area/chart-plot-area-converter';
import type { DeepPartial, DeepReadonly } from 'ts-essentials';
import type { IChartProps, IChartObjectProps, DeltaOf, IChartData, IChartObjectData, ICTColorStyle, STSchemeColorVal } from '@tencent/slide-sdk-model';
import type { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import type { IChartBaseShapeProps } from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-shape-info-converter';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { IColorStyle, IColorVariation } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';

/**
 * 图表独有属性
 */
export class ChartConverter {
    /**
     * 转换最上层的图表数据，对应chartSpace
     * uiModel将chartSpace的属性打平到了chartModel上（chartModel本身对应的chartObject没有样式属性）
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IChartProps>>,
        uiData: IChartModel,
    ): void {
        const sdkChartSpace = sdkData.chartSpace;
        if (!sdkChartSpace) {
            return;
        }

        // 核心图表数据chartObject
        if (sdkChartSpace.chart) {
            this.convertChartObjectDataSdkToUi(sdkChartSpace.chart, uiData);
        }

        // 下面为其他chartSpace属性
        if (sdkChartSpace.style) {
            uiData.style = sdkChartSpace.style.val;
        }

        uiData.chartSpace = uiData.chartSpace || {};
        if (sdkChartSpace.colorStyle) {
            uiData.chartSpace.colorStyle = uiData.chartSpace.colorStyle || {};
            this.convertChartColorStyleDataSdkToUi(sdkChartSpace.colorStyle, uiData.chartSpace.colorStyle);
        }
        // 样式
        ChartShapeInfoConverter.convertPartialDataFromSdkToUi(
            sdkChartSpace as IChartBaseShapeProps,
            uiData.chartSpace as unknown as IShapeModel,
        );
    }

    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IChartModel>,
        sdkData: DeltaOf<IChartData>,
    ) {
        sdkData.chartSpace = sdkData.chartSpace ?? {};
        sdkData.chartSpace.chart = sdkData.chartSpace.chart ?? {};

        // 核心图表数据chartObject
        this.convertChartObjectDataUiToSdk(uiData, sdkData.chartSpace.chart);

        // 下面为其他chartSpace属性

        // 在组合、多选的时候，有很多上层的handler会通过调用updateProps修改style
        // 而图表的style字段是图表独有的属性，与shape的style含义不同
        // 这里参照office的结果（多选元素设置样式，图表不生效），当style并非number类型时不做修改
        if (uiData.style && typeof uiData.style === 'number') {
            sdkData.chartSpace.style = {
                val: uiData.style,
            };
        }

        if (uiData.chartSpace) {
            if (!uiData.chartSpace.colorStyle) {
                sdkData.chartSpace.colorStyle = null;
            } else {
                sdkData.chartSpace.colorStyle = sdkData.chartSpace.colorStyle ?? {};
                this.convertChartColorStyleDataUiToSdk(uiData.chartSpace.colorStyle, sdkData.chartSpace.colorStyle);
            }
            // 样式
            ChartShapeInfoConverter.convertPartialDataFromUiToSdk(
                uiData.chartSpace as unknown as IShapeModel,
                sdkData.chartSpace as IChartBaseShapeProps,
            );
        }
    }

    /**
     * 转换核心的图表数据chartObject，对应chartSpace.chart
     */
    public static convertChartObjectDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IChartObjectProps>>,
        uiData: IChartModel,
    ): void {
        if (sdkData.title) {
            uiData.title = uiData.title || {};
            ChartTitleConverter.convertPartialDataSdkToUi(sdkData.title, uiData.title);
        } else {
            delete uiData.title;
        }

        if (sdkData.plotArea) {
            uiData.plotArea = uiData.plotArea || {};
            ChartPlotAreaConverter.convertPartialDataSdkToUi(sdkData.plotArea, uiData.plotArea);
        }

        if (sdkData.legend) {
            uiData.legend = uiData.legend || {};
            ChartLegendConverter.convertPartialDataSdkToUi(sdkData.legend, uiData.legend);
        } else {
            delete uiData.legend;
        }

        if (sdkData.dispBlanksAs) {
            uiData.dispBlanksAs = sdkData.dispBlanksAs.val;
        }
    }

    // eslint-disable-next-line complexity
    public static convertChartObjectDataUiToSdk(
        uiData: DeepPartial<IChartModel>,
        sdkData: DeltaOf<IChartObjectData>,
    ) {
        if (uiData.title !== undefined) {
            if (!uiData.title) {
                sdkData.title = null;
            } else {
                sdkData.title = sdkData.title ?? {};
                ChartTitleConverter.convertPartialDataUiToSdk(uiData.title, sdkData.title);
            }
        }

        if (uiData.plotArea !== undefined) {
            if (!uiData.plotArea) {
                sdkData.plotArea = null;
            } else {
                sdkData.plotArea = sdkData.plotArea ?? {};
                ChartPlotAreaConverter.convertPartialDataUiToSdk(uiData.plotArea, sdkData.plotArea);
            }
        }

        if (uiData.legend !== undefined) {
            if (!uiData.legend) {
                sdkData.legend = null;
            } else {
                sdkData.legend = sdkData.legend ?? {};
                ChartLegendConverter.convertPartialDataUiToSdk(uiData.legend, sdkData.legend);
            }
        }

        if (uiData.dispBlanksAs !== undefined) {
            if (!uiData.dispBlanksAs) {
                sdkData.dispBlanksAs = null;
            } else {
                sdkData.dispBlanksAs = {
                    val: uiData.dispBlanksAs,
                };
            }
        }
    }

    public static convertChartColorStyleDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTColorStyle>>,
        uiData: IColorStyle,
    ) {
        if (sdkData.id) {
            uiData.id = sdkData.id;
        }
        if (sdkData.meth) {
            uiData.meth = sdkData.meth;
        }
        if (sdkData.egColorChoice) {
            // @ts-ignore
            uiData.schemeClrs = sdkData.egColorChoice
                .map(i => i?.schemeClr?.val);
        }
        if (sdkData.variation) {
            uiData.variations = sdkData.variation.map((i) => {
                const variation: IColorVariation = {};
                if (i && Array.isArray(i.egColorTransform)) {
                    i.egColorTransform.forEach((colorTransform) => {
                        const colorTransformKey = Object.keys(colorTransform)[0];
                        variation[colorTransformKey] = colorTransform[colorTransformKey]?.val;
                    });
                }
                return variation;
            });
        }
    }

    public static convertChartColorStyleDataUiToSdk(
        uiData: DeepPartial<IColorStyle>,
        sdkData: DeltaOf<ICTColorStyle>,
    ) {
        if (uiData.id !== undefined) {
            if (!uiData.id) {
                sdkData.id = null;
            } else {
                sdkData.id = uiData.id;
            }
        }
        if (uiData.meth !== undefined) {
            if (!uiData.meth) {
                sdkData.meth = null;
            } else {
                sdkData.meth = uiData.meth;
            }
        }
        if (uiData.schemeClrs !== undefined) {
            if (!uiData.schemeClrs) {
                sdkData.egColorChoice = [];
            } else {
                sdkData.egColorChoice = uiData.schemeClrs.map(i => ({ schemeClr: { val: i } }));
            }
        }
        if (uiData.variations !== undefined) {
            if (!uiData.variations) {
                sdkData.variation = [];
            } else {
                sdkData.variation = uiData.variations.map((i) => {
                    if (i && Object.keys(i).length) {
                        return {
                            egColorTransform: Object.keys(i).map(key => ({ [key]: { val: i[key] } })),
                        };
                    }
                    return {};
                });
            }
        }
    }
}
